<script setup lang="ts">
import { ref } from 'vue'
import { storeToRefs } from 'pinia'
import { useRouter } from 'vue-router'
import { useUserStore } from '@/store/user'
import { usePayrollStore } from '@/store/payroll-data'
import { useNotificationsStore } from '@/store/notifications'
import Sidebar from '../components/Sidebar.vue'
import Header from '../components/Header.vue'
import TrafficLight from '../components/TrafficLight.vue'
import ChangeRole from '../components/ChangeRole.vue'
import DToastStack from '../components/notifications/DToastStack.vue'
import { useField, useForm } from 'vee-validate'
import { object, string } from 'yup'
import { useI18n } from 'vue-i18n'
import {
  DModal,
  DTextField,
  Lock02Icon,
  EyeIcon,
  EyeOffIcon
} from 'front-library'

/* store, router, notifications */
const router = useRouter()
const store = useUserStore()
const storePayroll = usePayrollStore()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos componente Modal */
const showModal = ref(false)

/* Datos componente */
const isRolesChangeModalActive = ref(false)
const { sidebarMenu } = storeToRefs(store)
const showOldPassword = ref(true)
const showNewPassword = ref(true)
const showConfirmNewPassword = ref(true)
const wrongData = ref('')

// datos del usuario
const user = ref({
  role: store.user?.actualRole?.name,
  roleCode: store.user?.actualRole?.code,
  name:
    store.user?.name === '' ? t('layouts.home.emptyName') : store.user?.name,
  photoUrl: '',
  email: store.user?.email
})

// datos de la nomina del usuario
const payroll = ref(storePayroll.payroll ?? storePayroll.menuPayroll[0])

// nominas del usuario
const payrolls = ref(storePayroll.menuPayroll)

// datos de los roles del usuario
const roles = ref(store.user?.roles)

/* Método para cambiar de nómina */
const changePayroll = (selectPayroll: any) => {
  storePayroll.changeSelectedPayroll(selectPayroll)
  payroll.value = selectPayroll
  //window.location.reload()
}

/* Método para cambiar de rol */
const changeRole = (role: string) => {
  user.value.role = roles.value?.find((r: any) => r.code === role)?.name ?? ''
  user.value.roleCode = role
  // actualizar rol y permisos store
  if (store.user) {
    store.user.actualRole = roles.value?.find((r: any) => r.code === role)
  }
  if (store.user?.actualRole?.permissions) {
    store.permissions = store.user.actualRole.permissions
  }
  // redirect home
  void router.push({ path: '/home' })
  isRolesChangeModalActive.value = false
}

/* Método para cambiar de ruta */
const changeRoute = (route: string) => {
  void router.push({ path: route })
}

/* Método para abrir modal con roles disponibles */
const changeRoleEvent = () => {
  isRolesChangeModalActive.value = true
}

/* Método para abrir la configuración */
const changeConfig = () => {
  showModal.value = true
  const inputsPassword = document.querySelectorAll('.block-input')
  for (let x = 0; x < inputsPassword.length; x++) {
    ;(inputsPassword[x] as any).onpaste = function (e: any) {
      e.preventDefault()
      notifications.addNotification({
        title: t('layouts.home.forbiddenAction'),
        text: ``,
        type: 'error',
        time: 10000
      })
    }
    ;(inputsPassword[x] as any).oncopy = function (e: any) {
      e.preventDefault()
      notifications.addNotification({
        title: t('layouts.home.forbiddenAction'),
        text: ``,
        type: 'error',
        time: 10000
      })
    }
  }
}

/* Datos formulario */
const dataChangePassword = ref({
  userName: store.user?.name,
  userOldPassword: '',
  userNewPassword: '',
  userConfirmNewPassword: ''
})

// Esquema de validación campos obligatorios - Actualizar informacion del usuario
const validationSchema = object().shape({
  userName: string()
    .matches(
      /^[A-Za-z0-9À-ÿ\u00f1\u00d1\s-_&()]+$/,
      t('form.valid.alphanumeric')
    )
    .required(t('form.valid.required')),
  userNewPassword: string()
    .matches(
      /^(?=(?:.*\d))(?=.*[A-Z])(?=.*[a-z])(?=.*[;:.,_\-*!?¿¡/#$%&+|°"<>'])\S{8,64}$/g,
      t('form.valid.password')
    )
    .required(t('form.valid.required')),
  userConfirmNewPassword: string().when(
    'userNewPassword',
    (userNewPassword, schema) => {
      if (errors.value.userNewPassword === undefined) {
        return schema
          .equals(userNewPassword, t('form.valid.equalsPassword'))
          .required(t('form.valid.required'))
      } else {
        return schema
          .matches(
            /^(?=(?:.*\d))(?=.*[A-Z])(?=.*[a-z])(?=.*[;:.,_\-*!?¿¡/#$%&+|°"<>'])\S{8,64}$/g,
            t('form.valid.password')
          )
          .required(t('form.valid.required'))
      }
    }
  ),
  userOldPassword: string()
    .test(
      'is-equal',
      t('form.valid.passwordActual'),
      function checkEqual(oldPassword) {
        const { userNewPassword } = this.parent
        if (oldPassword === userNewPassword) {
          return false
        }
        return true
      }
    )
    .required(t('form.valid.required'))
})

// Uso del metodo de validación, retorno de errores y metodo para reiniciar formulario
const { handleSubmit, errors, resetForm } = useForm({
  initialValues: dataChangePassword.value,
  validationSchema
})
// Definicion de campos del formulario
const { value: userName } = useField<string>('userName')
const { value: userOldPassword } = useField<string>('userOldPassword')
const { value: userNewPassword } = useField<string>('userNewPassword')
const { value: userConfirmNewPassword } = useField<string>(
  'userConfirmNewPassword'
)
// Metodo que valida los campos obligatorios del formulario
const onSubmit = handleSubmit((values, actions) => {
  void updateDataUser(values)
})
const updateDataUser = async (values: any) => {
  try {
    const result = await store.changeNameAndPassword(
      values.userName,
      values.userOldPassword,
      values.userNewPassword
    )
    if (result) {
      showModal.value = false
      wrongData.value = ''
      resetForm({})
      notifications.addNotification({
        title: t('layouts.home.updateSuccess'),
        text: ``,
        type: 'success',
        time: 10000
      })
    }
  } catch (error: any) {
    switch (error.name) {
      case 'LimitExceededException':
        wrongData.value = t('layouts.home.exceededAttempts')
        break
      case 'NotAuthorizedException':
        wrongData.value = t('layouts.home.incorrectPassword')
        break
      default:
        wrongData.value = error.name
        break
    }
  }
}

/* Método para abrir redreccionar a soporte */
const changeSupport = () => {
  window.open('https://soporteapsproduccion.raiseaticket.com/support/#/newticket', '_blank')
  //window.open('https://main.d1wovi0nloskn.amplifyapp.com', '_blank')
}

/* Metodo para cerrar el componente Modal */
const closeModal = () => {
  showModal.value = false
}

/* Método para cerrar sesión */
const changeLogout = () => {
  store.logout()
  void router.replace({ name: 'Login' })
}
</script>
<template>
  <DModal
    size=""
    :form="true"
    :open="showModal"
    :acceptQuestion="t('layouts.home.update')"
    @closeModal="closeModal"
    @otherMethod="onSubmit">
    <template #form>
      <div>
        <div class="mb-3">
          <Lock02Icon
            class="p-2.5 m-auto h-12 w-12 bg-blue-100 text-blue-600 rounded-full" />
          <p class="mt-4 mb-1.5 text-xl font-semibold text-center">
            {{ t('layouts.home.updateInfo') }}
          </p>
          <p class="text-gray-500 text-sm text-center">
            {{ t('layouts.home.changeUserData') }}
          </p>
        </div>
        <div
          v-if="wrongData"
          class="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4"
          role="alert">
          <p class="text-sm font-bold">{{ wrongData }}</p>
        </div>
        <form class="grid w-96 px-3">
          <div class="grid relative mb-3">
            <DTextField
              :label="t('layouts.home.name')"
              :error="errors.userName"
              v-model="userName"
              :disabled="true" />
          </div>
          <div class="grid relative mb-3">
            <DTextField
              class-input="block-input"
              :label="t('layouts.home.currentPassword')"
              :type="showOldPassword ? 'password' : 'text'"
              :error="errors.userOldPassword"
              :placeholder="t('layouts.home.enterCurrentPassword')"
              :required="true"
              v-model="userOldPassword" />
            <div class="absolute right-3 top-8 z-50">
              <EyeIcon
                class="text-gray-600"
                @click="showOldPassword = !showOldPassword"
                :class="{
                  hidden: !showOldPassword,
                  block: showOldPassword
                }" />
              <EyeOffIcon
                class="text-gray-600"
                @click="showOldPassword = !showOldPassword"
                :class="{
                  block: !showOldPassword,
                  hidden: showOldPassword
                }" />
            </div>
          </div>
          <div class="grid relative mb-3">
            <DTextField
              class-input="block-input"
              :label="t('layouts.home.newPassword')"
              :type="showNewPassword ? 'password' : 'text'"
              :error="errors.userNewPassword"
              :placeholder="t('layouts.home.enterNewPassword')"
              :required="true"
              v-model="userNewPassword" />
            <div class="absolute right-3 top-8 z-50">
              <EyeIcon
                class="text-gray-600"
                @click="showNewPassword = !showNewPassword"
                :class="{
                  hidden: !showNewPassword,
                  block: showNewPassword
                }" />
              <EyeOffIcon
                class="text-gray-600"
                @click="showNewPassword = !showNewPassword"
                :class="{
                  block: !showNewPassword,
                  hidden: showNewPassword
                }" />
            </div>
          </div>
          <div class="grid relative mb-3">
            <DTextField
              class-input="block-input"
              :label="t('layouts.home.confirmNewPassword')"
              :type="showConfirmNewPassword ? 'password' : 'text'"
              :error="errors.userConfirmNewPassword"
              :placeholder="t('layouts.home.enterNewPasswordAgain')"
              :required="true"
              v-model="userConfirmNewPassword" />
            <div class="absolute right-3 top-8 z-50">
              <EyeIcon
                class="text-gray-600"
                @click="showConfirmNewPassword = !showConfirmNewPassword"
                :class="{
                  hidden: !showConfirmNewPassword,
                  block: showConfirmNewPassword
                }" />
              <EyeOffIcon
                class="text-gray-600"
                @click="showConfirmNewPassword = !showConfirmNewPassword"
                :class="{
                  block: !showConfirmNewPassword,
                  hidden: showConfirmNewPassword
                }" />
            </div>
          </div>
        </form>
      </div>
    </template>
  </DModal>
  <DToastStack class="left-14" />
  <main class="h-screen w-screen text-gray-900 bg-gray-50 flex">
    <Sidebar
      :menu="sidebarMenu"
      :user="user"
      @change:route="changeRoute"
      @change:role="changeRoleEvent"
      @support="changeSupport"
      @config="changeConfig"
      @logout="changeLogout" />
    <div class="w-full overflow-hidden mb-1">
      <div class="sm:flex justify-between items-center lg:block">
        <Header
          :payrolls="payrolls"
          :payroll="payroll"
          @change:payroll="changePayroll" />
        <TrafficLight />
      </div>
      <div class="w-full contents">
        <slot></slot>
      </div>
    </div>
  </main>
  <ChangeRole
    :modalActive="isRolesChangeModalActive"
    :roles="roles"
    :userRole="user.roleCode"
    @change:role="changeRole"
    @close="isRolesChangeModalActive = !isRolesChangeModalActive" />
</template>
